import { Timestamp } from 'firebase/firestore';

/**
 * Fraud Detection Service — SLIK POS
 *
 * Core principle: FRAUD ONLY WORKS WITH CASH.
 * A discount on a card payment doesn't put money in a barber's pocket —
 * the reduced amount still goes to the business bank account.
 * Cash is the ONLY payment method where a discount = money the barber can steal.
 *
 * Primary fraud patterns detected:
 * 1. Price edits DOWNWARD on cash transactions (barber collected higher amount, records lower)
 * 2. Cash + discount combos (discount amount = what barber pockets)
 * 3. Ghost transactions / abandoned checkouts (service done, cash collected, never rung up)
 * 4. Repeat offender escalation (barber with strikes still applying cash discounts)
 * 5. Pattern detection (multiple cash discounts same day, cash-only discount pattern)
 * 6. Digital coin manipulation (stopgap until wallet access control is built)
 */

// ==========================================
// INTERFACES & TYPES
// ==========================================

export type PaymentMethod = 'cash' | 'card' | 'digital_coins' | 'digital_wallet' | 'mixed' | 'other';
export type DiscountReason = 'unsatisfied_client' | 'service_issue' | 'loyalty' | 'grandfather_pricing' | 'other';
export type FraudSeverity = 'none' | 'low' | 'medium' | 'high' | 'critical';

/**
 * Discount request to be analyzed for fraud
 */
export interface DiscountRequest {
  // Transaction Identity
  barberId: string;
  barberName: string;
  appointmentId: string;
  clientId: string;
  clientName: string;

  // Discount Details
  originalPrice: number;
  discountedPrice: number;
  discountAmount: number;
  discountPercent: number;
  discountReason: DiscountReason;
  reasonNotes?: string;

  // Transaction Context
  paymentMethod: PaymentMethod;
  serviceStartTime: Date | Timestamp;
  serviceEndTime: Date | Timestamp;
  requestTime: Date | Timestamp;

  // Price Edit Detection
  isTransactionEdited?: boolean;        // Has this transaction been modified after creation?
  editCount?: number;                   // How many times has it been edited?
  wasPaymentCollected?: boolean;        // Was payment already collected before discount applied?
  originalTransactionAmount?: number;   // What was the original amount before edits?

  // Edit Direction (new — critical for determining if edit is legitimate)
  // Price went UP = legitimate correction/upsell. Price went DOWN = potential fraud.
  // If not provided, calculated from originalTransactionAmount vs discountedPrice.
  editDirection?: 'up' | 'down' | 'none';

  // Appointment Status (new — editing a completed appointment is critical)
  isCompletedAppointment?: boolean;

  // Barber History
  barberStrikeCount: number;

  // Same-Day Context (new — pattern detection within a single shift)
  sameDayCashDiscountCount?: number;    // How many cash discounts this barber gave today already
  cashDiscountRateVsCard?: number;      // % of cash txns discounted vs % of card txns discounted (7-day)

  // Client Context (new)
  isNewClient?: boolean;                // First-time client (easier target for fraud)
  clientHasGrandfatherTag?: boolean;    // Client profile has grandfather pricing tag

  // Digital Coins (stopgap — should be removed once wallet access control is built)
  digitalCoinsApplied?: number;
  clientDigitalCoinsBalance?: number;
  cashCollectedAmount?: number;
}

/**
 * Result of fraud analysis
 */
export interface FraudAnalysisResult {
  // Overall Assessment
  isSuspicious: boolean;
  riskScore: number; // 0-100
  severity: FraudSeverity;

  // Detected Patterns
  fraudFlags: string[];

  // Risk Breakdown
  riskFactors: {
    transactionManipulationRisk: number; // 0-50 points
    paymentMethodRisk: number;           // 0-50 points
    discountSizeRisk: number;            // 0-55 points
    barberHistoryRisk: number;           // 0-15 points
    patternRisk: number;                 // 0-30 points
    timingRisk: number;                  // 0-40 points — checkout completed long AFTER the appointment window
  };

  // Human-Readable Analysis
  reasoning: string[];
  recommendations: string[];

  // Metadata
  analyzedAt: Date;
}

/**
 * Pending checkout detection (for abandoned service / ghost transaction fraud)
 */
export interface PendingCheckoutData {
  barberId: string;
  barberName: string;
  appointmentId: string;
  clientName: string;
  serviceType: string;
  serviceStartTime: Date;
  expectedDuration: number; // minutes
  minutesOverdue: number;
  isOverdue: boolean;
  isCritical: boolean; // > 30 min overdue
}

// ==========================================
// CONFIGURATION
// ==========================================

export interface FraudDetectionConfig {
  // Risk Score Thresholds
  lowRiskThreshold: number;
  mediumRiskThreshold: number;
  highRiskThreshold: number;
  criticalRiskThreshold: number;

  // Category 1: Price Edit Risk Weights
  // Only edits that LOWER the price matter. Price going UP = legitimate correction.
  normalCheckoutRisk: number;           // 0 pts — no edits
  priceDecreasedCashRisk: number;       // 40 pts — price lowered + cash payment (core fraud)
  priceDecreasedCardRisk: number;       // 10 pts — price lowered + card (not theft, but unauthorized)
  multipleDownwardEditsRisk: number;    // 50 pts — multiple edits all lowering price
  editAfterCompletedRisk: number;       // 50 pts — editing a completed/closed transaction
  transactionEditedRisk: number;        // 25 pts — legacy: generic edit flag (when direction unknown)
  multipleEditsRisk: number;            // 40 pts — legacy: multiple edits (direction unknown)
  postPaymentDiscountRisk: number;      // 50 pts — discount after payment collected

  // Category 2: Payment Method Risk
  // Core insight: fraud only works with cash. Card discounts aren't theft.
  cashWithDiscountRisk: number;         // 35 pts — cash + any discount = core fraud signal
  cashNoDiscountRisk: number;           // 5 pts — cash alone is not suspicious
  cardRisk: number;                     // 0 pts — card = electronic trail, can't pocket
  digitalCoinsRisk: number;             // 10 pts — potential manipulation
  mixedRisk: number;                    // 15 pts — complex reconciliation
  cashPlusDigitalCoinsRisk: number;     // 50 pts — collected cash + used client coins = pocket cash

  // Category 3: Discount Size Thresholds
  // These point values are for CASH transactions. Card transactions get 25% of these points.
  smallDiscountPercent: number;         // <10% — acceptable (5 pts cash / 1 pt card)
  mediumDiscountPercent: number;        // 10-20% — theft emerging (20 pts cash / 5 pts card)
  largeDiscountPercent: number;         // 20-30% — serious theft (30 pts cash / 8 pts card)
  criticalDiscountPercent: number;      // 30%+ — extreme fraud (40 pts cash / 10 pts card)
  dollarAmountThreshold: number;        // $10+ — auto-flag on cash regardless of %
  cardDiscountMultiplier: number;       // 0.25 — card discounts get 25% of cash risk points

  // Category 4: Barber History
  repeatOffenderBonus: number;          // +5 per previous strike
  strikeWithCashDiscountMultiplier: number; // 1.5x — barber with strikes still doing cash discounts

  // Category 5: Pattern Detection
  busyTimesMultiplier: number;
  sameDayCashDiscountThreshold: number; // 2+ cash discounts same day = flag
  sameDayCashDiscountRisk: number;      // 15 pts
  cashOnlyDiscountPatternRisk: number;  // 20 pts — discounts only on cash, never card
  newClientDiscountRisk: number;        // 5 pts — discount to first-time client

  // Category 6: Checkout Timing Window (anti-fraud — barber can only check out within the appt window)
  // A checkout completed long AFTER the appointment ended is suspicious: back-dated / off-hours /
  // next-day closes let a barber manipulate attribution, tips, or ring up ghost tickets. The RISK
  // (below) always scores; the interceptor/UI gate (checkoutWindowEnforced) turns "past window" into
  // "manager-only". Owner/Manager roles bypass. Walk-ins (checkout ≈ now) score 0 → unaffected.
  checkoutGraceMinutes: number;             // ≤ this many min after service end → 0 risk (normal run-over)
  checkoutStaleMinutes: number;             // grace..this → "late" (lateCheckoutRisk); beyond → "very late"
  lateCheckoutRisk: number;                 // 35 pts — moderately late checkout
  veryLateCheckoutRisk: number;             // 40 pts — very late checkout (hours after)
  checkoutWindowEnforced: boolean;          // gate on: past checkoutManagerRequiredMinutes = manager-only + flag
  checkoutManagerRequiredMinutes: number;   // past the window by this many min → non-managers are blocked
}

export const DEFAULT_FRAUD_CONFIG: FraudDetectionConfig = {
  lowRiskThreshold: 30,
  mediumRiskThreshold: 60,
  highRiskThreshold: 70, // was 79 → a 1-point "high" band (only score==79). 70-79=high, >=80=critical. Audit fix.
  criticalRiskThreshold: 80,

  // Category 1: Price Edit Risk
  normalCheckoutRisk: 0,
  priceDecreasedCashRisk: 40,
  priceDecreasedCardRisk: 10,
  multipleDownwardEditsRisk: 50,
  editAfterCompletedRisk: 50,
  transactionEditedRisk: 25,        // Fallback when direction unknown
  multipleEditsRisk: 40,            // Fallback when direction unknown
  postPaymentDiscountRisk: 50,

  // Category 2: Payment Method
  cashWithDiscountRisk: 35,          // Cash + discount = the core fraud signal
  cashNoDiscountRisk: 5,             // Cash alone is normal
  cardRisk: 0,                       // Card payments can't be pocketed
  digitalCoinsRisk: 10,
  mixedRisk: 15,
  cashPlusDigitalCoinsRisk: 50,      // Cash collected + coins used = pocket cash fraud

  // Category 3: Discount Size (STRICT per owner feedback)
  // "20% = $20 in pocket per cash client. 5 clients/day x $22 = $110/day = $29K/year theft"
  smallDiscountPercent: 10,
  mediumDiscountPercent: 20,
  largeDiscountPercent: 30,
  criticalDiscountPercent: 40,
  dollarAmountThreshold: 10,         // "$10 automatically flags on the cash discount"
  cardDiscountMultiplier: 0.25,      // Card discounts get 25% of risk points (not theft, just unauthorized)

  // Category 4: Barber History
  repeatOffenderBonus: 5,
  strikeWithCashDiscountMultiplier: 1.5, // 1.5x risk if barber has strikes AND is discounting cash

  // Category 5: Pattern Detection
  busyTimesMultiplier: 1.1,
  sameDayCashDiscountThreshold: 2,
  sameDayCashDiscountRisk: 15,
  cashOnlyDiscountPatternRisk: 20,
  newClientDiscountRisk: 5,

  // Category 6: Checkout Timing Window
  checkoutGraceMinutes: 15,               // up to 15 min after the appt ends is normal
  checkoutStaleMinutes: 60,               // 15–60 min late = flagged; >60 min = very late
  lateCheckoutRisk: 35,
  veryLateCheckoutRisk: 40,
  checkoutWindowEnforced: true,           // enforce the manager gate for egregiously-late checkouts
  checkoutManagerRequiredMinutes: 120,    // >2h past the appointment window → manager-only (safe default)
};

// ==========================================
// CORE FRAUD DETECTION
// ==========================================

/** Normalize a Date | Firestore Timestamp to a plain Date. */
const toDate = (t: Date | Timestamp): Date =>
  t instanceof Timestamp ? t.toDate() : t;

/**
 * Checkout-timing risk (0–40). How long AFTER the appointment/service ENDED was this checkout
 * completed? Within grace → 0. Moderately late → lateCheckoutRisk. Very late (hours) → veryLateCheckoutRisk.
 * Checking out DURING or BEFORE the service (negative lateness) is always 0 — never penalize on-time work.
 */
export const computeCheckoutTimingRisk = (
  serviceEndTime: Date | Timestamp,
  requestTime: Date | Timestamp,
  config: FraudDetectionConfig = DEFAULT_FRAUD_CONFIG
): number => {
  const end = toDate(serviceEndTime).getTime();
  const now = toDate(requestTime).getTime();
  if (!Number.isFinite(end) || !Number.isFinite(now)) return 0; // bad data → fail-open (never block)
  const minutesLate = (now - end) / 60000;
  if (minutesLate <= config.checkoutGraceMinutes) return 0;      // on time / normal run-over / future
  if (minutesLate <= config.checkoutStaleMinutes) return config.lateCheckoutRisk;
  return config.veryLateCheckoutRisk;
};

export interface CheckoutWindowVerdict {
  minutesLate: number;      // minutes after service end (negative = checked out during/before service)
  timingRisk: number;       // 0–40, same curve as computeCheckoutTimingRisk
  isPastWindow: boolean;    // beyond the grace period
  requiresManager: boolean; // enforced AND beyond checkoutManagerRequiredMinutes → non-managers blocked
}

/**
 * Standalone checkout-window verdict used by the checkout UI/interceptor to gate EVERY checkout
 * (discounted or not). "Past the window" past the manager threshold → only an Owner/Manager may
 * complete it, and it's flagged for audit. Fails open on bad/missing times.
 */
export const evaluateCheckoutWindow = (
  serviceEndTime: Date | Timestamp | undefined | null,
  now: Date | Timestamp = new Date(),
  config: FraudDetectionConfig = DEFAULT_FRAUD_CONFIG
): CheckoutWindowVerdict => {
  if (!serviceEndTime) return { minutesLate: 0, timingRisk: 0, isPastWindow: false, requiresManager: false };
  const end = toDate(serviceEndTime).getTime();
  const nowMs = toDate(now).getTime();
  if (!Number.isFinite(end) || !Number.isFinite(nowMs)) {
    return { minutesLate: 0, timingRisk: 0, isPastWindow: false, requiresManager: false };
  }
  const minutesLate = (nowMs - end) / 60000;
  const timingRisk = computeCheckoutTimingRisk(serviceEndTime, now, config);
  return {
    minutesLate,
    timingRisk,
    isPastWindow: minutesLate > config.checkoutGraceMinutes,
    requiresManager: config.checkoutWindowEnforced && minutesLate > config.checkoutManagerRequiredMinutes,
  };
};

export const analyzeDiscountRequest = (
  request: DiscountRequest,
  config: FraudDetectionConfig = DEFAULT_FRAUD_CONFIG
): FraudAnalysisResult => {
  const fraudFlags: string[] = [];
  const reasoning: string[] = [];
  const recommendations: string[] = [];
  const isCash = request.paymentMethod === 'cash';

  const riskFactors = {
    transactionManipulationRisk: 0,
    paymentMethodRisk: 0,
    discountSizeRisk: 0,
    barberHistoryRisk: 0,
    patternRisk: 0,
    timingRisk: 0,
  };

  // ==================================================
  // 1. PRICE EDIT ANALYSIS (0-50 points)
  // Core idea: only price DECREASES matter. Price going UP = legitimate.
  // ==================================================
  const editAnalysis = analyzePriceEdits(request, config);
  riskFactors.transactionManipulationRisk = editAnalysis.risk;
  fraudFlags.push(...editAnalysis.flags);
  reasoning.push(...editAnalysis.reasoning);

  // ==================================================
  // 2. PAYMENT METHOD ANALYSIS (0-50 points)
  // Core idea: cash + discount = theft. Card + discount = bad business, not theft.
  // ==================================================
  const paymentAnalysis = analyzePaymentMethod(request, config);
  riskFactors.paymentMethodRisk = paymentAnalysis.risk;
  fraudFlags.push(...paymentAnalysis.flags);
  reasoning.push(...paymentAnalysis.reasoning);

  // ==================================================
  // 3. DISCOUNT SIZE ANALYSIS (0-55 points)
  // Points weighted by payment method: full for cash, 25% for card.
  // ==================================================
  const discountAnalysis = analyzeDiscountSize(request, config);
  riskFactors.discountSizeRisk = discountAnalysis.risk;
  fraudFlags.push(...discountAnalysis.flags);
  reasoning.push(...discountAnalysis.reasoning);

  // ==================================================
  // 4. BARBER HISTORY ANALYSIS (0-15 points)
  // Strikes + still discounting cash = escalation multiplier.
  // ==================================================
  const historyAnalysis = analyzeBarberHistory(request, config);
  riskFactors.barberHistoryRisk = historyAnalysis.risk;
  fraudFlags.push(...historyAnalysis.flags);
  reasoning.push(...historyAnalysis.reasoning);

  // ==================================================
  // 5. PATTERN ANALYSIS (0-30 points)
  // Multiple cash discounts same day, cash-only discount pattern, new client targeting.
  // ==================================================
  const patternAnalysis = analyzePatterns(request, config);
  riskFactors.patternRisk = patternAnalysis.risk;
  fraudFlags.push(...patternAnalysis.flags);
  reasoning.push(...patternAnalysis.reasoning);

  // ==================================================
  // 6. DIGITAL COINS (stopgap — remove once wallet access control is built)
  // ==================================================
  if (request.digitalCoinsApplied && request.digitalCoinsApplied > 0) {
    const coinsAnalysis = analyzeDigitalCoins(request);
    if (coinsAnalysis.isSuspicious) {
      fraudFlags.push('digital_coin_manipulation');
      reasoning.push(coinsAnalysis.reasoning);
      riskFactors.patternRisk += 10;
    }
  }

  // ==================================================
  // 7. CHECKOUT TIMING ANALYSIS (0-40 points)
  // A checkout completed long after the appointment ended is suspicious (back-dated / off-hours).
  // ==================================================
  riskFactors.timingRisk = computeCheckoutTimingRisk(request.serviceEndTime, request.requestTime, config);
  if (riskFactors.timingRisk > 0) {
    const minsLate = Math.round((toDate(request.requestTime).getTime() - toDate(request.serviceEndTime).getTime()) / 60000);
    fraudFlags.push('post_service_discount');
    reasoning.push(`Checkout completed ${minsLate} minutes after service completed — verify this is not a back-dated or off-hours close`);
  }

  // ==================================================
  // TOTAL RISK SCORE
  // ==================================================
  let totalRisk = Math.min(100, Object.values(riskFactors).reduce((sum, r) => sum + r, 0));

  // ==================================================
  // SEVERITY + RECOMMENDATIONS
  // ==================================================
  let severity: FraudSeverity = 'none';
  if (totalRisk >= config.criticalRiskThreshold) {
    severity = 'critical';
    recommendations.push('BLOCK TRANSACTION - High fraud risk detected');
    recommendations.push('Notify manager and owner immediately');
    recommendations.push('Investigate barber conduct');
  } else if (totalRisk >= config.highRiskThreshold) {
    severity = 'high';
    recommendations.push('Require manager approval before proceeding');
    recommendations.push('Flag transaction for audit');
    recommendations.push('Review barber discount history');
  } else if (totalRisk >= config.mediumRiskThreshold) {
    severity = 'medium';
    recommendations.push('Manager approval recommended');
    recommendations.push('Log transaction for monitoring');
  } else if (totalRisk >= config.lowRiskThreshold) {
    severity = 'low';
    recommendations.push('Auto-approve with logging');
    recommendations.push('Monitor for pattern development');
  } else {
    severity = 'none';
    recommendations.push('Approve - Normal discount request');
  }

  // A very-late checkout (hours after the appointment ended) is a strong back-dating / off-hours signal.
  // Escalate to critical so the discounted checkout is manager-gated regardless of the other factors.
  if (config.checkoutWindowEnforced && riskFactors.timingRisk >= config.veryLateCheckoutRisk && severity !== 'critical') {
    severity = 'critical';
    recommendations.unshift('CRITICAL: Checkout completed long after the appointment ended — manager verification required (possible back-dated / off-hours close)');
  }

  // Priority recommendations for critical patterns
  if (fraudFlags.includes('cash_collected_coins_used')) {
    recommendations.unshift('CRITICAL FRAUD: Barber collected cash but used client digital coins - pocket cash fraud detected');
  }
  if (fraudFlags.includes('price_decreased_cash')) {
    recommendations.unshift('HIGH RISK: Price lowered on cash transaction - barber may have collected original amount and pocketed the difference');
  }
  if (fraudFlags.includes('edit_after_completed')) {
    recommendations.unshift('CRITICAL: Transaction edited AFTER marked completed - why is a closed transaction being modified?');
  }
  if (fraudFlags.includes('multiple_downward_edits')) {
    recommendations.unshift('CRITICAL: Multiple price reductions on same transaction - active manipulation');
  }
  if (isCash && fraudFlags.includes('same_day_cash_discounts')) {
    recommendations.unshift('PATTERN: Multiple cash discounts by same barber today - systematic skimming suspected');
  }
  if (fraudFlags.includes('cash_only_discount_pattern')) {
    recommendations.unshift('SMOKING GUN: Barber gives discounts to cash clients but never to card clients - deliberate theft pattern');
  }

  return {
    isSuspicious: totalRisk >= config.mediumRiskThreshold,
    riskScore: Math.round(totalRisk),
    severity,
    fraudFlags,
    riskFactors,
    reasoning,
    recommendations,
    analyzedAt: new Date(),
  };
};

// ==========================================
// CATEGORY 1: PRICE EDIT ANALYSIS
// ==========================================

/**
 * Analyze price edits on the transaction.
 *
 * Real scenarios:
 * - LEGITIMATE: Appointment booked as "New Client $45", actual service was "Signature Cut $50"
 *   → barber corrects price UP. Not fraud. 0 points.
 * - FRAUD: Client pays $50 cash. Barber edits service to "Kids Cut $25".
 *   → barber pockets $25. Price went DOWN + cash = high risk.
 * - CRITICAL: Transaction already marked "completed". Someone goes back and edits it.
 *   → why? This should never happen in normal operations.
 */
function analyzePriceEdits(
  request: DiscountRequest,
  config: FraudDetectionConfig
): { risk: number; flags: string[]; reasoning: string[] } {
  const flags: string[] = [];
  const reasoning: string[] = [];
  let risk = 0;
  const isCash = request.paymentMethod === 'cash';

  // Determine edit direction
  let direction = request.editDirection || 'none';
  if (direction === 'none' && request.originalTransactionAmount && request.originalTransactionAmount > 0) {
    if (request.discountedPrice < request.originalTransactionAmount) direction = 'down';
    else if (request.discountedPrice > request.originalTransactionAmount) direction = 'up';
  }

  // CRITICAL: Edit after appointment already completed
  if (request.isCompletedAppointment === true && request.isTransactionEdited === true) {
    risk = config.editAfterCompletedRisk; // 50 pts
    flags.push('edit_after_completed');
    reasoning.push('CRITICAL: Transaction edited after appointment was already marked completed — closed transactions should not be modified');
    return { risk, flags, reasoning };
  }

  // CRITICAL: Multiple edits all lowering the price
  if (request.editCount && request.editCount >= 2 && direction === 'down') {
    risk = config.multipleDownwardEditsRisk; // 50 pts
    flags.push('multiple_downward_edits');
    const changeAmount = request.originalTransactionAmount
      ? `$${request.originalTransactionAmount.toFixed(2)} → $${request.discountedPrice.toFixed(2)}`
      : `${request.editCount} downward edits`;
    reasoning.push(`CRITICAL: Price lowered ${request.editCount} times (${changeAmount}) — active price manipulation`);
    return { risk, flags, reasoning };
  }

  // Multiple edits but direction unknown (legacy fallback)
  if (request.editCount && request.editCount >= 2 && direction === 'none') {
    risk = config.multipleEditsRisk; // 40 pts
    flags.push('multiple_edits');
    reasoning.push(`Transaction edited ${request.editCount} times — suspicious manipulation (could not determine price direction)`);
    return { risk, flags, reasoning };
  }

  // Price went DOWN — risk depends on payment method
  if (direction === 'down') {
    if (isCash) {
      risk = config.priceDecreasedCashRisk; // 40 pts
      flags.push('price_decreased_cash');
      const diff = request.originalTransactionAmount
        ? `reduced by $${(request.originalTransactionAmount - request.discountedPrice).toFixed(2)}`
        : 'price lowered';
      reasoning.push(`Price edited DOWN on cash transaction (${diff}) — barber may have collected original amount and pocketed the difference`);
    } else {
      risk = config.priceDecreasedCardRisk; // 10 pts
      flags.push('price_decreased_card');
      reasoning.push('Price edited down on card transaction — not theft (card already charged) but unauthorized price change');
    }
    return { risk, flags, reasoning };
  }

  // Single edit with unknown direction (legacy: isTransactionEdited without amount data)
  if (request.isTransactionEdited === true && direction === 'none') {
    risk = isCash ? config.transactionEditedRisk : Math.round(config.transactionEditedRisk * 0.25); // 25 cash / 6 card
    flags.push('transaction_edited');
    reasoning.push(isCash
      ? 'Transaction edited on cash payment — unable to determine if price went up or down, treating as elevated risk'
      : 'Transaction edited on card payment — lower risk since card trail exists');
    return { risk, flags, reasoning };
  }

  // Price went UP — legitimate correction, 0 risk
  if (direction === 'up') {
    risk = 0;
    reasoning.push('Price edited UP (upsell or service correction) — legitimate, no fraud risk');
    return { risk, flags, reasoning };
  }

  // No edits at all
  risk = config.normalCheckoutRisk; // 0 pts
  reasoning.push('No price edits detected — normal checkout');
  return { risk, flags, reasoning };
}

// ==========================================
// CATEGORY 2: PAYMENT METHOD ANALYSIS
// ==========================================

/**
 * Analyze payment method in context of discounts.
 *
 * Core insight: fraud only works with cash.
 * - Cash + discount = barber pockets the discount amount. THIS IS THE SIGNAL.
 * - Card + discount = business loses revenue but barber can't pocket it. Not theft.
 * - Cash + digital coins = barber collects cash, uses client coins to "pay" in system. Worst case.
 */
function analyzePaymentMethod(
  request: DiscountRequest,
  config: FraudDetectionConfig
): { risk: number; flags: string[]; reasoning: string[] } {
  const flags: string[] = [];
  const reasoning: string[] = [];
  let risk = 0;
  const hasDiscount = request.discountAmount > 0;

  // CRITICAL: Cash collected + digital coins used (pocket cash fraud)
  if (request.cashCollectedAmount && request.cashCollectedAmount > 0 &&
      request.digitalCoinsApplied && request.digitalCoinsApplied > 0) {
    risk = config.cashPlusDigitalCoinsRisk; // 50 pts
    flags.push('cash_collected_coins_used');
    reasoning.push(`CRITICAL FRAUD: Barber collected $${request.cashCollectedAmount.toFixed(2)} cash but applied ${request.digitalCoinsApplied} digital coins from client balance — pocketed the cash`);
    return { risk, flags, reasoning };
  }

  switch (request.paymentMethod) {
    case 'cash':
      if (hasDiscount) {
        risk = config.cashWithDiscountRisk; // 35 pts — this is the core fraud signal
        flags.push('cash_with_discount');
        reasoning.push(`Cash payment with $${request.discountAmount.toFixed(2)} discount — discount amount is what barber can pocket`);
      } else {
        risk = config.cashNoDiscountRisk; // 5 pts — cash alone is normal
        reasoning.push('Cash payment without discount — normal transaction');
      }
      break;

    case 'card':
    case 'digital_wallet':
      risk = config.cardRisk; // 0 pts
      if (hasDiscount) {
        reasoning.push(`${request.paymentMethod === 'card' ? 'Card' : 'Digital wallet'} payment with discount — not theft (money goes to business account) but may be unauthorized discounting`);
      } else {
        reasoning.push(`${request.paymentMethod === 'card' ? 'Card' : 'Digital wallet'} payment — electronic trail, no fraud risk`);
      }
      break;

    case 'digital_coins':
      risk = config.digitalCoinsRisk; // 10 pts
      if (hasDiscount) {
        flags.push('coins_with_discount');
        reasoning.push('Digital coins payment with discount — monitor for manipulation');
      } else {
        reasoning.push('Digital coins payment — low risk');
      }
      break;

    case 'mixed':
      risk = config.mixedRisk; // 15 pts
      flags.push('mixed_payment');
      reasoning.push('Mixed payment method — harder to reconcile, moderate risk');
      break;

    default:
      reasoning.push('Unknown payment method');
      break;
  }

  return { risk, flags, reasoning };
}

// ==========================================
// CATEGORY 3: DISCOUNT SIZE ANALYSIS
// ==========================================

/**
 * Analyze discount size — weighted by payment method.
 *
 * Key rule: same percentage thresholds, but CASH gets full points, CARD gets 25%.
 * A 20% discount on cash = $20 the barber can pocket from a $100 ticket.
 * A 20% discount on card = $20 less revenue but barber gets nothing.
 *
 * Business math (owner's numbers):
 * - $40 average ticket, 20 clients/day, 5 pay cash (25%)
 * - If barber steals $20 per cash client: $100/day = $26K/year
 * - If barber steals $10 per cash client: $50/day = $13K/year
 */
function analyzeDiscountSize(
  request: DiscountRequest,
  config: FraudDetectionConfig
): { risk: number; flags: string[]; reasoning: string[] } {
  const flags: string[] = [];
  const reasoning: string[] = [];
  let risk = 0;

  const isCash = request.paymentMethod === 'cash';
  const multiplier = isCash ? 1.0 : config.cardDiscountMultiplier; // 1.0 for cash, 0.25 for card

  const discountPercent = request.discountPercent ||
    ((request.discountAmount / request.originalPrice) * 100);
  const dollarAmount = request.discountAmount;
  const discountStr = `$${dollarAmount.toFixed(2)}`;

  // Dollar amount auto-flag (only meaningful for cash)
  if (isCash && dollarAmount >= config.dollarAmountThreshold) {
    flags.push('dollar_threshold_exceeded');
    reasoning.push(`Cash discount of ${discountStr} exceeds $${config.dollarAmountThreshold} threshold — auto-flagged (${discountStr} is what barber can pocket)`);
    risk += 15; // Bonus on top of percentage-based risk
  }

  // Percentage-based risk (scaled by payment method)
  if (discountPercent >= config.criticalDiscountPercent) {
    risk += Math.round(40 * multiplier);
    flags.push('critical_discount');
    if (isCash) {
      reasoning.push(`CRITICAL: ${discountPercent.toFixed(1)}% cash discount (${discountStr}) — extreme fraud, pocketing most of the ticket`);
    } else {
      reasoning.push(`Large ${discountPercent.toFixed(1)}% discount on card (${discountStr}) — unauthorized but not theft (money still goes to business)`);
    }
  } else if (discountPercent >= config.largeDiscountPercent) {
    risk += Math.round(30 * multiplier);
    flags.push('large_discount');
    if (isCash) {
      const dailyTheft = (dollarAmount * 5).toFixed(2);
      reasoning.push(`HIGH RISK: ${discountPercent.toFixed(1)}% cash discount (${discountStr}) — serious theft pattern (${discountStr} x 5 cash clients/day = $${dailyTheft}/day)`);
    } else {
      reasoning.push(`${discountPercent.toFixed(1)}% discount on card (${discountStr}) — large revenue loss but not pocketable`);
    }
  } else if (discountPercent >= config.mediumDiscountPercent) {
    risk += Math.round(20 * multiplier);
    flags.push('medium_discount');
    if (isCash) {
      reasoning.push(`ELEVATED: ${discountPercent.toFixed(1)}% cash discount (${discountStr}) — theft pattern emerging`);
    } else {
      reasoning.push(`${discountPercent.toFixed(1)}% discount on card (${discountStr}) — moderate, monitor for authorization`);
    }
  } else if (discountPercent > 0) {
    risk += Math.round(5 * multiplier);
    if (discountPercent >= config.smallDiscountPercent) {
      flags.push('small_discount');
    }
    reasoning.push(`${discountPercent.toFixed(1)}% discount (${discountStr}) — within acceptable range`);
  } else {
    reasoning.push('No discount applied');
  }

  return { risk, flags, reasoning };
}

// ==========================================
// CATEGORY 4: BARBER HISTORY ANALYSIS
// ==========================================

/**
 * Analyze barber's strike history with escalation logic.
 *
 * Key addition: if a barber has prior strikes AND is currently applying a cash discount,
 * multiply the history risk by 1.5x. They've been caught before and they're still doing it.
 */
function analyzeBarberHistory(
  request: DiscountRequest,
  config: FraudDetectionConfig
): { risk: number; flags: string[]; reasoning: string[] } {
  const flags: string[] = [];
  const reasoning: string[] = [];
  let risk = 0;

  const strikeCount = request.barberStrikeCount || 0;
  const isCash = request.paymentMethod === 'cash';
  const hasDiscount = request.discountAmount > 0;

  if (strikeCount >= 2) {
    risk = 10;
    flags.push('repeat_offender');
    reasoning.push(`Barber has ${strikeCount} previous strikes (repeat offender)`);
  } else if (strikeCount >= 1) {
    risk = 5;
    flags.push('has_warnings');
    reasoning.push(`Barber has ${strikeCount} previous warning`);
  } else {
    risk = 0;
    reasoning.push('Barber has clean record (no previous strikes)');
  }

  // Escalation: barber with strikes still applying cash discounts
  if (strikeCount > 0 && isCash && hasDiscount) {
    const originalRisk = risk;
    risk = Math.round(risk * config.strikeWithCashDiscountMultiplier);
    flags.push('strike_escalation');
    reasoning.push(`ESCALATION: Barber with ${strikeCount} strike(s) is still applying cash discounts — risk multiplied from ${originalRisk} to ${risk}`);
  }

  return { risk, flags, reasoning };
}

// ==========================================
// CATEGORY 5: PATTERN ANALYSIS
// ==========================================

/**
 * Analyze behavioral patterns that indicate systematic fraud.
 *
 * Real scenarios:
 * - Barber gives discounts to 3 cash clients in one day. Each one alone is small.
 *   Together = systematic skimming.
 * - Barber ONLY discounts cash clients, never card. Dead giveaway — they know
 *   they can only steal from cash transactions.
 * - Discounts during rush hours when manager can't watch every transaction.
 * - Discounts to new clients who don't know the real price.
 */
function analyzePatterns(
  request: DiscountRequest,
  config: FraudDetectionConfig
): { risk: number; flags: string[]; reasoning: string[] } {
  const flags: string[] = [];
  const reasoning: string[] = [];
  let risk = 0;

  // Same-day multiple cash discounts
  if (request.sameDayCashDiscountCount !== undefined &&
      request.sameDayCashDiscountCount >= config.sameDayCashDiscountThreshold) {
    risk += config.sameDayCashDiscountRisk; // 15 pts
    flags.push('same_day_cash_discounts');
    reasoning.push(`Barber has given ${request.sameDayCashDiscountCount} cash discounts today already — systematic skimming pattern`);
  }

  // Cash-only discount pattern (discounts cash clients but not card clients)
  if (request.cashDiscountRateVsCard !== undefined && request.cashDiscountRateVsCard > 50) {
    risk += config.cashOnlyDiscountPatternRisk; // 20 pts
    flags.push('cash_only_discount_pattern');
    reasoning.push(`Barber discounts cash clients ${request.cashDiscountRateVsCard}% more often than card clients — deliberate theft pattern (they know they can only pocket cash)`);
  }

  // Busy time (harder to monitor)
  const requestTime = request.requestTime instanceof Timestamp
    ? request.requestTime.toDate()
    : request.requestTime;
  const hour = requestTime.getHours();
  const day = requestTime.getDay();
  const isWeekend = day === 0 || day === 6;
  const isBusyTime = (hour >= 17 && hour <= 20) || (hour >= 11 && hour <= 13) || isWeekend;
  if (isBusyTime) {
    risk += 5;
    flags.push('busy_time');
    reasoning.push('Transaction during busy hours/weekend (harder to monitor)');
  }

  // Vague discount reason
  if (request.discountReason === 'other') {
    risk += 5;
    flags.push('vague_reason');
    reasoning.push('Discount reason marked as "other" — vague justification');
  }

  // New client targeting
  if (request.isNewClient === true && request.discountAmount > 0 && request.paymentMethod === 'cash') {
    risk += config.newClientDiscountRisk; // 5 pts
    flags.push('new_client_cash_discount');
    reasoning.push('Cash discount given to first-time client — new clients are easier targets (they don\'t know the real price)');
  }

  // Grandfather pricing — legitimate IF client has the tag
  if (request.discountReason === 'grandfather_pricing') {
    if (request.clientHasGrandfatherTag === true) {
      risk = Math.max(0, risk - 10);
      reasoning.push('Grandfather pricing with verified client tag — pre-approved, legitimate discount');
    } else {
      risk += 10;
      flags.push('unverified_grandfather');
      reasoning.push('Grandfather pricing claimed but client has NO grandfather tag in profile — barber may be using this as a cover');
    }
  }

  return { risk, flags, reasoning };
}

// ==========================================
// DIGITAL COINS (stopgap)
// ==========================================

/**
 * Analyze digital coins usage for manipulation.
 *
 * NOTE: This entire category should be removed once proper wallet access control
 * is implemented. The fix is: SLIK coins can only be applied from the client's
 * phone/portal wallet, not from the POS terminal by the barber. The barber should
 * not have the ability to touch a client's coin balance.
 */
function analyzeDigitalCoins(request: DiscountRequest): { isSuspicious: boolean; reasoning: string } {
  if (!request.digitalCoinsApplied || request.digitalCoinsApplied === 0) {
    return { isSuspicious: false, reasoning: 'No digital coins used' };
  }

  if (request.paymentMethod === 'cash' && request.digitalCoinsApplied > 0) {
    return {
      isSuspicious: true,
      reasoning: 'Digital coins applied with cash payment — potential double-dipping (client charged full cash, coins also deducted from their balance)',
    };
  }

  return { isSuspicious: false, reasoning: 'Digital coins usage appears normal' };
}

// ==========================================
// PENDING CHECKOUT DETECTION
// ==========================================

/**
 * Check if a service is pending checkout (ghost transaction / abandoned service fraud).
 *
 * This catches the BIGGEST fraud: barber does the service, collects cash,
 * and never rings it up in the system. The money disappears completely.
 *
 * Should be called before allowing any new checkout to start for this barber.
 */
export const detectPendingCheckout = (
  barberId: string,
  serviceStartTime: Date,
  expectedDurationMinutes: number
): PendingCheckoutData | null => {
  const now = new Date();
  const expectedEndTime = new Date(serviceStartTime.getTime() + expectedDurationMinutes * 60000);
  const minutesOverdue = (now.getTime() - expectedEndTime.getTime()) / (1000 * 60);

  const isOverdue = minutesOverdue > 15;
  const isCritical = minutesOverdue > 30;

  if (isOverdue) {
    return {
      barberId,
      barberName: '',
      appointmentId: '',
      clientName: '',
      serviceType: '',
      serviceStartTime,
      expectedDuration: expectedDurationMinutes,
      minutesOverdue: Math.round(minutesOverdue),
      isOverdue,
      isCritical,
    };
  }

  return null;
};

// ==========================================
// RISK SCORE UTILITIES
// ==========================================

export const getSeverityLabel = (severity: FraudSeverity): string => {
  const labels = {
    none: 'No Risk',
    low: 'Low Risk',
    medium: 'Medium Risk',
    high: 'High Risk',
    critical: 'CRITICAL RISK',
  };
  return labels[severity];
};

export const getSeverityColor = (severity: FraudSeverity): string => {
  const colors = {
    none: 'green',
    low: 'blue',
    medium: 'yellow',
    high: 'orange',
    critical: 'red',
  };
  return colors[severity];
};

export const getSeverityBadgeClass = (severity: FraudSeverity): string => {
  const classes = {
    none: 'bg-green-100 text-green-800',
    low: 'bg-blue-100 text-blue-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800 font-bold',
  };
  return classes[severity];
};
